// BUILTIN_CLASS_EXTENSIONS_FOCUS_V2
import {
  calculateAttendanceContribution,
  normalizeAttendanceSettings
} from '../../../utils/attendanceCalculations';
import { getEffectiveToday } from '../../../utils/systemTime';
import { addDaysToDateString } from '../../../utils/assignmentTiming';
import {
  buildProgressAssessmentUnits,
  collectProgressSubmissionEnrolmentIds,
  fetchProgressCqManualMarks,
  fetchProgressSubmissionData,
  latestProgressFeedback,
  loadProgressAssignmentContext
} from '../../../utils/studentProgressAssessmentUnits';
// TRADITIONAL_CORE_WEIGHTS_PATCH_20260813
// TRADITIONAL_GRADING_PATCH_20260813
import { useState, useEffect } from 'react';
import { useStudentDataRefreshTick } from '../../../utils/studentDataRefresh';

import { isProgressSubmissionComplete } from '../../../utils/studentProgressAssessmentUnits';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*&order=week_number.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchAttendance(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance?enrolment_id=eq.${enrolmentId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchAttendanceRequiredWeekIds(enrolmentId, courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const membershipResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_memberships?enrolment_id=eq.${enrolmentId}&select=attendance_group_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}&limit=1`,
    { headers }
  );

  const memberships = await membershipResponse.json();
  const membership = Array.isArray(memberships)
    ? memberships[0]
    : null;

  if (!membership?.attendance_group_id) return [];

  const weeksResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_weeks?attendance_group_id=eq.${membership.attendance_group_id}&select=course_week_id`,
    { headers }
  );

  const rows = await weeksResponse.json();

  return (Array.isArray(rows) ? rows : [])
    .map(row => row.course_week_id)
    .filter(Boolean);
}

async function fetchCqSubmissions(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?enrolment_id=eq.${enrolmentId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchPdSubmissions(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_submissions?enrolment_id=eq.${enrolmentId}&select=*,pd_feedback(*)`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchAssignmentTypes() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_types?select=id,name,is_core`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchSelfEvalResponses(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_responses?enrolment_id=eq.${enrolmentId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchPdFeedback(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_feedback?select=*,pd_submissions!inner(week_id,enrolment_id)&pd_submissions.enrolment_id=eq.${enrolmentId}`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchAssignmentWeights(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_weights?course_id=eq.${courseId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}


async function fetchAssignmentGroupGradeOverrides(
  enrolmentId
) {
  if (!enrolmentId) {
    return [];
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/assignment_group_grade_overrides' +
        '?enrolment_id=eq.' +
        encodeURIComponent(
          enrolmentId
        ) +
        '&select=assignment_submission_id,score',
      {
        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Loading individual group-grade overrides failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const rows =
    await response.json();

  return Array.isArray(rows)
    ? rows
    : [];
}


async function fetchQiAssignmentsForStudent(
  courseId
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/cq_question_assignments?select=*,course_weeks!inner(course_id,class_date,cq_enabled)&course_weeks.course_id=eq.${courseId}`,
      {
        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Loading QI assignments failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const rows =
    await response.json();

  return Array.isArray(
    rows
  )
    ? rows
    : [];
}


async function fetchQiSubmissionsForStudent(
  enrolmentId
) {
  const headers = {
    'apikey':
      SUPABASE_KEY,

    'Authorization':
      'Bearer ' +
      SUPABASE_KEY
  };


  /*
   * First identify the course belonging
   * to this enrolment.
   */
  const enrolmentResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/enrolments?id=eq.${encodeURIComponent(
        enrolmentId
      )}&select=course_id&limit=1`,
      {
        headers
      }
    );

  if (
    !enrolmentResponse.ok
  ) {
    const detail =
      await enrolmentResponse
        .text();

    throw new Error(
      'Loading QI course failed (' +
        enrolmentResponse.status +
        '): ' +
        detail
    );
  }

  const enrolmentRows =
    await enrolmentResponse
      .json();

  const courseId =
    Array.isArray(
      enrolmentRows
    )
      ? enrolmentRows[0]
          ?.course_id
      : null;

  if (!courseId) {
    return [];
  }


  /*
   * We need all assignment IDs for the
   * course because this student may have
   * temporarily worked with another group.
   */
  const assignments =
    await fetchQiAssignmentsForStudent(
      courseId
    );

  const assignmentIds =
    assignments
      .map(
        assignment =>
          assignment.id
      )
      .filter(Boolean);

  if (
    !assignmentIds.length
  ) {
    return [];
  }


  const baseUrl =
    `${SUPABASE_URL}/rest/v1/cq_question_improvements?cq_question_assignment_id=in.(${assignmentIds.join(
      ','
    )})&select=*`;


  /*
   * Rows where this student is the owner.
   */
  const ownerResponse =
    await fetch(
      baseUrl +
        '&enrolment_id=eq.' +
        encodeURIComponent(
          enrolmentId
        ),
      {
        headers
      }
    );

  if (
    !ownerResponse.ok
  ) {
    const detail =
      await ownerResponse
        .text();

    throw new Error(
      'Loading owned QI submissions failed (' +
        ownerResponse.status +
        '): ' +
        detail
    );
  }

  const ownerRows =
    await ownerResponse
      .json();


  /*
   * Rows where another group member saved
   * the work but this student's enrolment ID
   * appears in group_member_ids.
   *
   * Try JSON-array containment first.
   * If the column is represented as a native
   * PostgreSQL array, retry using array syntax.
   */
  async function fetchParticipantRows(
    containmentValue
  ) {
    return fetch(
      baseUrl +
        '&group_member_ids=cs.' +
        encodeURIComponent(
          containmentValue
        ),
      {
        headers
      }
    );
  }


  let participantResponse =
    await fetchParticipantRows(
      JSON.stringify([
        String(
          enrolmentId
        )
      ])
    );


  if (
    !participantResponse.ok
  ) {
    participantResponse =
      await fetchParticipantRows(
        '{' +
          String(
            enrolmentId
          ) +
          '}'
      );
  }


  if (
    !participantResponse.ok
  ) {
    const detail =
      await participantResponse
        .text();

    throw new Error(
      'Loading group QI participation failed (' +
        participantResponse.status +
        '): ' +
        detail
    );
  }


  const participantRows =
    await participantResponse
      .json();


  /*
   * Owner and participant queries may return
   * the same row. De-duplicate them.
   */
  const byId =
    new Map();


  [
    ...(
      Array.isArray(
        ownerRows
      )
        ? ownerRows
        : []
    ),

    ...(
      Array.isArray(
        participantRows
      )
        ? participantRows
        : []
    )
  ].forEach(
    row => {
      if (row?.id) {
        byId.set(
          String(
            row.id
          ),
          row
        );
      }
    }
  );


  return [
    ...byId.values()
  ];
}


function qiSubmissionIncludesEnrolment(
  submission,
  enrolmentId
) {
  const targetId =
    String(
      enrolmentId
    );

  if (
    String(
      submission
        ?.enrolment_id ||
      ''
    ) === targetId
  ) {
    return true;
  }

  return (
    Array.isArray(
      submission
        ?.group_member_ids
    ) &&
    submission
      .group_member_ids
      .some(
        id =>
          String(id) ===
          targetId
      )
  );
}


function getSeriesPdDueDate(
  course,
  week,
  submission = null
) {
  if (!week?.class_date) {
    return null;
  }

  let baseDueDate =
    String(
      week.class_date
    ).slice(0, 10);

  if (course?.pd_due_day) {
    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'
    ];

    const targetDay =
      dayNames.indexOf(
        course.pd_due_day
      );

    if (targetDay >= 0) {
      const parts =
        baseDueDate
          .split('-')
          .map(Number);

      const date =
        new Date(
          parts[0],
          parts[1] - 1,
          parts[2],
          12,
          0,
          0
        );

      while (
        date.getDay() !==
        targetDay
      ) {
        date.setDate(
          date.getDate() + 1
        );
      }

      baseDueDate = [
        date.getFullYear(),
        String(
          date.getMonth() + 1
        ).padStart(2, '0'),
        String(
          date.getDate()
        ).padStart(2, '0')
      ].join('-');
    }
  }

  return addDaysToDateString(
    baseDueDate,
    Math.max(
      0,
      Number(
        submission
          ?.extension_days
      ) || 0
    )
  );
}


function getSeriesContinueContexts(
  contexts,
  currentCourseId
) {
  const ordered =
    (Array.isArray(contexts)
      ? contexts
      : [])
      .filter(
        context =>
          context?.course?.id &&
          context?.enrolment?.id
      )
      .sort((a, b) =>
        String(
          a.course
            ?.semesters
            ?.start_date ||
          ''
        ).localeCompare(
          String(
            b.course
              ?.semesters
              ?.start_date ||
            ''
          )
        )
      );

  const currentIndex =
    ordered.findIndex(
      context =>
        String(
          context.course.id
        ) ===
        String(currentCourseId)
    );

  if (currentIndex < 0) {
    return [];
  }

  let startIndex =
    currentIndex;

  while (
    startIndex > 0 &&
    ordered[startIndex]
      .course
      ?.series_progress_mode ===
      'continue'
  ) {
    startIndex -= 1;
  }

  return ordered.slice(
    startIndex,
    currentIndex + 1
  );
}


async function loadSeriesProgressSnapshot(
  context,
  today
) {
  const linkedCourse =
    context?.course;

  const linkedEnrolment =
    context?.enrolment;

  if (
    !linkedCourse?.id ||
    !linkedEnrolment?.id
  ) {
    return null;
  }

  const progressEnrolment = {
    ...linkedEnrolment,
    course_id:
      linkedEnrolment
        .course_id ||
      linkedCourse.id
  };

  const assignmentContext =
    await loadProgressAssignmentContext({
      courseId:
        linkedCourse.id,

      enrolments: [
        progressEnrolment
      ]
    });

  const submissionEnrolmentIds =
    collectProgressSubmissionEnrolmentIds({
      enrolments: [
        progressEnrolment
      ],

      context:
        assignmentContext
    });

  const [
    weeksRaw,
    attendanceRaw,
    cqRaw,
    cqManualRaw,
    pdRaw,
    progressSubmissionData,
    qiAssignmentsRaw,
    qiSubmissionsRaw,
    requiredWeekIdsRaw
  ] =
    await Promise.all([
      fetchWeeks(
        linkedCourse.id
      ),

      linkedCourse
        .attendance_enabled ===
        false
        ? Promise.resolve([])
        : fetchAttendance(
            linkedEnrolment.id
          ),

      linkedCourse
        .cq_enabled ===
        false
        ? Promise.resolve([])
        : fetchCqSubmissions(
            linkedEnrolment.id
          ),

      linkedCourse
        .cq_enabled ===
        false
        ? Promise.resolve([])
        : fetchProgressCqManualMarks([
            linkedEnrolment.id
          ]),

      linkedCourse
        .pd_enabled ===
        false
        ? Promise.resolve([])
        : fetchPdSubmissions(
            linkedEnrolment.id
          ),

      fetchProgressSubmissionData(
        submissionEnrolmentIds
      ),

      linkedCourse
        .qi_enabled ===
        true
        ? fetchQiAssignmentsForStudent(
            linkedCourse.id,
            linkedEnrolment.stream,
            linkedEnrolment.group_number
          )
        : Promise.resolve([]),

      linkedCourse
        .qi_enabled ===
        true
        ? fetchQiSubmissionsForStudent(
            linkedEnrolment.id
          )
        : Promise.resolve([]),

      linkedCourse
        .group_based_attendance &&
      linkedCourse
        .attendance_enabled !==
        false
        ? fetchAttendanceRequiredWeekIds(
            linkedEnrolment.id,
            linkedCourse.id
          )
        : Promise.resolve([])
    ]);

  const weeks =
    Array.isArray(weeksRaw)
      ? weeksRaw
      : [];

  const attendanceRows =
    Array.isArray(attendanceRaw)
      ? attendanceRaw
      : [];

  const cqRows =
    Array.isArray(cqRaw)
      ? cqRaw
      : [];

  const cqManualRows =
    Array.isArray(cqManualRaw)
      ? cqManualRaw
      : [];

  const pdRows =
    Array.isArray(pdRaw)
      ? pdRaw
      : [];

  const qiAssignments =
    Array.isArray(
      qiAssignmentsRaw
    )
      ? qiAssignmentsRaw
      : [];

  const qiSubmissions =
    Array.isArray(
      qiSubmissionsRaw
    )
      ? qiSubmissionsRaw
      : [];

  const requiredWeekIds =
    new Set(
      Array.isArray(
        requiredWeekIdsRaw
      )
        ? requiredWeekIdsRaw
        : []
    );

  const attendanceWeeks =
    linkedCourse
      .attendance_enabled ===
      false
      ? []
      : (
          linkedCourse
            .group_based_attendance
            ? weeks.filter(
                week =>
                  requiredWeekIds
                    .has(
                      week.id
                    )
              )
            : weeks.filter(
                week =>
                  week
                    .attendance_enabled
              )
        );

  const attendancePast =
    attendanceWeeks
      .filter(
        week =>
          week.class_date <=
          today
      );

  const attendancePastIds =
    new Set(
      attendancePast
        .map(
          week =>
            week.id
        )
    );

  const attended =
    new Set(
      attendanceRows
        .filter(
          row =>
            row.present &&
            attendancePastIds
              .has(
                row.week_id
              )
        )
        .map(
          row =>
            row.week_id
        )
    ).size;

  const cqWeeks =
    linkedCourse
      .cq_enabled ===
      false
      ? []
      : weeks.filter(
          week =>
            week
              .cq_enabled
        );

  const cqPast =
    cqWeeks.filter(
      week =>
        week.class_date <=
        today
    );

  const cqPastIds =
    new Set(
      cqPast.map(
        week =>
          week.id
      )
    );

  const cqSubmitted =
    new Set([
      ...cqRows
        .filter(
          row =>
            cqPastIds
              .has(
                row.week_id
              )
        )
        .map(
          row =>
            row.week_id
        ),

      ...cqManualRows
        .filter(
          row =>
            cqPastIds
              .has(
                row.week_id
              )
        )
        .map(
          row =>
            row.week_id
        )
    ]).size;

  const pdWeeks =
    linkedCourse
      .pd_enabled ===
      false
      ? []
      : weeks.filter(
          week =>
            week
              .pd_enabled
        );

  const pdPast =
    pdWeeks
      .filter(
        week => {
          const submission =
            pdRows.find(
              row =>
                row.week_id ===
                week.id
            );

          const dueDate =
            getSeriesPdDueDate(
              linkedCourse,
              week,
              submission
            );

          return (
            !!dueDate &&
            dueDate <= today
          );
        }
      );

  const pdPastIds =
    new Set(
      pdPast.map(
        week =>
          week.id
      )
    );

  const pdSubmitted =
    new Set(
      pdRows
        .filter(
          row =>
            isProgressSubmissionComplete(row, row.pd_feedback) &&
            pdPastIds
              .has(
                row.week_id
              )
        )
        .map(
          row =>
            row.week_id
        )
    ).size;

  const qiRelevant =
    linkedCourse
      .qi_enabled ===
      true &&
    linkedCourse
      .cq_enabled !==
      false
      ? qiAssignments.filter(
          assignment =>
            assignment
              .course_weeks
              ?.cq_enabled !==
            false
        )
      : [];


  const qiById =
    new Map(
      qiRelevant.map(
        assignment => [
          String(
            assignment.id
          ),
          assignment
        ]
      )
    );


  /*
   * Any QI row on which the student is
   * recorded counts as their participation,
   * regardless of which group owned it.
   */
  const qiParticipantSubmissions =
    qiSubmissions.filter(
      submission =>
        qiSubmissionIncludesEnrolment(
          submission,
          linkedEnrolment.id
        ) &&
        qiById.has(
          String(
            submission
              .cq_question_assignment_id
          )
        )
    );


  /*
   * Begin with their nominal group's QI weeks.
   */
  const qiAllWeeks =
    new Set(
      qiRelevant
        .filter(
          assignment =>
            Number(
              assignment.stream
            ) ===
              Number(
                linkedEnrolment
                  .stream
              ) &&
            Number(
              assignment
                .group_number
            ) ===
              Number(
                linkedEnrolment
                  .group_number
              )
        )
        .map(
          assignment =>
            assignment
              .course_week_id
        )
    );


  /*
   * If they actually participated with another
   * group, that week also belongs to them.
   */
  qiParticipantSubmissions
    .forEach(
      submission => {
        const assignment =
          qiById.get(
            String(
              submission
                .cq_question_assignment_id
            )
          );

        if (
          assignment
            ?.course_week_id
        ) {
          qiAllWeeks.add(
            assignment
              .course_week_id
          );
        }
      }
    );


  const qiPastWeeks =
    new Set(
      qiRelevant
        .filter(
          assignment =>
            qiAllWeeks.has(
              assignment
                .course_week_id
            ) &&
            assignment
              .course_weeks
              ?.class_date &&
            assignment
              .course_weeks
              .class_date <=
              today
        )
        .map(
          assignment =>
            assignment
              .course_week_id
        )
    );


  const qiSubmitted =
    new Set(
      qiParticipantSubmissions
        .filter(
          submission =>
            submission
              .submitted ===
            true
        )
        .map(
          submission =>
            qiById
              .get(
                String(
                  submission
                    .cq_question_assignment_id
                )
              )
              ?.course_week_id
        )
        .filter(
          weekId =>
            weekId &&
            qiPastWeeks.has(
              weekId
            )
        )
    ).size;

  const units =
    buildProgressAssessmentUnits({
      courseId:
        linkedCourse.id,

      enrolment:
        progressEnrolment,

      context:
        assignmentContext,

      submissions:
        progressSubmissionData
          .submissions,

      completions:
        progressSubmissionData
          .completions
    });

  const assignmentProgress =
    {};

  units.forEach(
    unit => {
      const name =
        unit.typeName ||
        'Assignment';

      if (
        !assignmentProgress[
          name
        ]
      ) {
        assignmentProgress[
          name
        ] = {
          completed: 0,
          soFar: 0,
          total: 0
        };
      }

      const progress =
        assignmentProgress[
          name
        ];

      const complete =
        unit
          .requiresSubmission ===
          false
          ? unit.completed
          : unit.completed;

      progress.total +=
        1;

      if (complete) {
        progress.completed +=
          1;
      }

      if (
        complete ||
        (
          unit.dueDate &&
          String(
            unit.dueDate
          ).slice(0, 10) <=
            today
        )
      ) {
        progress.soFar +=
          1;
      }
    }
  );

  return {
    semester:
      linkedCourse
        ?.semesters
        ?.name ||
      'Semester',

    attendance: {
      completed:
        attended,
      soFar:
        attendancePast.length,
      total:
        attendanceWeeks.length
    },

    cq: {
      completed:
        cqSubmitted,
      soFar:
        cqPast.length,
      total:
        cqWeeks.length
    },

    qi: {
      completed:
        qiSubmitted,
      soFar:
        qiPastWeeks.size,
      total:
        qiAllWeeks.size
    },

    pd: {
      completed:
        pdSubmitted,
      soFar:
        pdPast.length,
      total:
        pdWeeks.length
    },

    assignments:
      assignmentProgress
  };
}


function combineSeriesProgressSnapshots(
  snapshots
) {
  const combined = {
    semesters: [],
    attendance: {
      completed: 0,
      soFar: 0,
      total: 0
    },
    cq: {
      completed: 0,
      soFar: 0,
      total: 0
    },
    qi: {
      completed: 0,
      soFar: 0,
      total: 0
    },
    pd: {
      completed: 0,
      soFar: 0,
      total: 0
    },
    assignments: {}
  };

  (Array.isArray(snapshots)
    ? snapshots
    : [])
    .filter(Boolean)
    .forEach(snapshot => {
      combined.semesters.push(
        snapshot.semester
      );

      [
        'attendance',
        'cq',
        'qi',
        'pd'
      ].forEach(key => {
        combined[key].completed +=
          Number(
            snapshot[key]
              ?.completed ||
            0
          );

        combined[key].soFar +=
          Number(
            snapshot[key]
              ?.soFar ||
            0
          );

        combined[key].total +=
          Number(
            snapshot[key]
              ?.total ||
            0
          );
      });

      Object.entries(
        snapshot.assignments ||
        {}
      ).forEach(
        ([name, progress]) => {
          if (
            !combined
              .assignments[name]
          ) {
            combined
              .assignments[name] = {
                completed: 0,
                soFar: 0,
                total: 0
              };
          }

          combined
            .assignments[name]
            .completed +=
            Number(
              progress.submitted ||
              0
            );

          combined
            .assignments[name]
            .soFar +=
            Number(
              progress.soFar ||
              0
            );

          combined
            .assignments[name]
            .total +=
            Number(
              progress.total ||
              0
            );
        }
      );
    });

  return combined;
}
export default function StudentProgress({
  session,
  course,
  enrolment,
  seriesContexts = []
}) {
  const [weeks, setWeeks] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [attendanceRequiredWeekIds, setAttendanceRequiredWeekIds] =
    useState([]);
  const [cqSubmissions, setCqSubmissions] = useState([]);

  const [
    cqManualSubmissionMarks,
    setCqManualSubmissionMarks
  ] = useState([]);

  const [pdSubmissions, setPdSubmissions] = useState([]);
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([]);

  const [
    assignmentGradeOverrides,
    setAssignmentGradeOverrides
  ] = useState([]);

  const [
    assignmentCompletions,
    setAssignmentCompletions
  ] = useState([]);

  const [
    progressAssignmentContext,
    setProgressAssignmentContext
  ] = useState(null);

  const [selfEvalResponses, setSelfEvalResponses] = useState([]);
  const [pdFeedback, setPdFeedback] = useState([]);
  const [assignmentWeights, setAssignmentWeights] = useState([]);
  const [assignmentTypeSettings, setAssignmentTypeSettings] =
    useState([]);
  const [loading, setLoading] = useState(true);
  const [qiAssignments, setQiAssignments] = useState([]);
  const [qiSubmissions, setQiSubmissions] = useState([]);
  const [
    seriesProgress,
    setSeriesProgress
  ] = useState(null);
  const [
    seriesProgressLoading,
    setSeriesProgressLoading
  ] = useState(false);
  const [
    seriesProgressError,
    setSeriesProgressError
  ] = useState('');
  const refreshTick = useStudentDataRefreshTick();

  useEffect(() => {
    let cancelled =
      false;

    async function loadCurrentProgress() {
      setLoading(true);

      try {
        const progressEnrolment = {
          id:
            enrolment.id,

          course_id:
            enrolment.course_id ||
            course.id
        };

        const progressContext =
          await loadProgressAssignmentContext({
            courseId:
              course.id,

            enrolments: [
              progressEnrolment
            ]
          });

        const submissionEnrolmentIds =
          collectProgressSubmissionEnrolmentIds({
            enrolments: [
              progressEnrolment
            ],

            context:
              progressContext
          });

        const [
          w,
          att,
          cq,
          cqManual,
          pd,
          progressSubmissionData,
          gradeOverrides,
          types,
          se,
          pf,
          aw,
          qa,
          qs,
          attendanceWeekIds
        ] =
          await Promise.all([
            fetchWeeks(
              course.id
            ),

            course
              .attendance_enabled ===
              false
              ? Promise.resolve([])
              : fetchAttendance(
                  enrolment.id
                ),

            course
              .cq_enabled ===
              false
              ? Promise.resolve([])
              : fetchCqSubmissions(
                  enrolment.id
                ),

            course
              .cq_enabled ===
              false
              ? Promise.resolve([])
              : fetchProgressCqManualMarks([
                  enrolment.id
                ]),

            course
              .pd_enabled ===
              false
              ? Promise.resolve([])
              : fetchPdSubmissions(
                  enrolment.id
                ),

            fetchProgressSubmissionData(
              submissionEnrolmentIds
            ),

            fetchAssignmentGroupGradeOverrides(
              enrolment.id
            ),

            fetchAssignmentTypes(),

            fetchSelfEvalResponses(
              enrolment.id
            ),

            fetchPdFeedback(
              enrolment.id
            ),

            fetchAssignmentWeights(
              course.id
            ),

            course
              .qi_enabled ===
              true
              ? fetchQiAssignmentsForStudent(
                  course.id,
                  enrolment.stream,
                  enrolment.group_number
                )
              : Promise.resolve([]),

            course
              .qi_enabled ===
              true
              ? fetchQiSubmissionsForStudent(
                  enrolment.id
                )
              : Promise.resolve([]),

            course
              .group_based_attendance &&
            course
              .attendance_enabled !==
              false
              ? fetchAttendanceRequiredWeekIds(
                  enrolment.id,
                  course.id
                )
              : Promise.resolve([])
          ]);

        if (cancelled) {
          return;
        }

        setWeeks(
          Array.isArray(w)
            ? w
            : []
        );

        setAttendance(
          Array.isArray(att)
            ? att
            : []
        );

        setAttendanceRequiredWeekIds(
          Array.isArray(
            attendanceWeekIds
          )
            ? attendanceWeekIds
            : []
        );

        setCqSubmissions(
          Array.isArray(cq)
            ? cq
            : []
        );

        setCqManualSubmissionMarks(
          Array.isArray(
            cqManual
          )
            ? cqManual
            : []
        );

        setPdSubmissions(
          Array.isArray(pd)
            ? pd
            : []
        );

        setAssignmentTypes(
          Array.isArray(types)
            ? types
            : []
        );

        setCourseAssignments(
          progressContext
            .assignments ||
          []
        );

        setAssignmentSubmissions(
          progressSubmissionData
            .submissions ||
          []
        );

        setAssignmentGradeOverrides(
          Array.isArray(
            gradeOverrides
          )
            ? gradeOverrides
            : []
        );

        setAssignmentCompletions(
          progressSubmissionData
            .completions ||
          []
        );

        setProgressAssignmentContext(
          progressContext
        );

        setSelfEvalResponses(
          Array.isArray(se)
            ? se
            : []
        );

        setPdFeedback(
          Array.isArray(pf)
            ? pf
            : []
        );

        setAssignmentWeights(
          Array.isArray(aw)
            ? aw
            : []
        );

        setAssignmentTypeSettings(
          progressContext
            .assignmentTypeSettings ||
          []
        );

        setQiAssignments(
          Array.isArray(qa)
            ? qa
            : []
        );

        setQiSubmissions(
          Array.isArray(qs)
            ? qs
            : []
        );

      } catch (error) {
        console.error(
          'Loading student progress failed:',
          error
        );

      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCurrentProgress();

    return () => {
      cancelled =
        true;
    };
  }, [
    course.id,
    course.attendance_enabled,
    course.cq_enabled,
    course.pd_enabled,
    course.qi_enabled,
    course.group_based_attendance,
    enrolment.id,
    enrolment.course_id,
    enrolment.stream,
    enrolment.group_number,
    refreshTick
  ]);


  useEffect(() => {
    let cancelled = false;

    async function loadSeriesProgress() {
      const shouldContinue =
        course
          ?.series_progress_mode ===
          'continue' &&
        course
          ?.grading_mode !==
          'traditional';

      if (!shouldContinue) {
        setSeriesProgress(null);
        setSeriesProgressError('');
        setSeriesProgressLoading(false);
        return;
      }

      const contexts =
        getSeriesContinueContexts(
          seriesContexts,
          course.id
        );

      if (contexts.length <= 1) {
        setSeriesProgress(null);
        setSeriesProgressError('');
        setSeriesProgressLoading(false);
        return;
      }

      setSeriesProgressLoading(true);
      setSeriesProgressError('');

      try {
        const snapshots =
          await Promise.all(
            contexts.map(
              context =>
                loadSeriesProgressSnapshot(
                  context,
                  getEffectiveToday()
                )
            )
          );

        if (cancelled) {
          return;
        }

        setSeriesProgress(
          combineSeriesProgressSnapshots(
            snapshots
          )
        );
      } catch (error) {
        console.error(
          'Loading cumulative course-series progress failed:',
          error
        );

        if (!cancelled) {
          setSeriesProgress(null);
          setSeriesProgressError(
            error?.message ||
            'Could not load cumulative progress.'
          );
        }
      } finally {
        if (!cancelled) {
          setSeriesProgressLoading(false);
        }
      }
    }

    loadSeriesProgress();

    return () => {
      cancelled = true;
    };
  }, [
    course.id,
    course.grading_mode,
    course.series_progress_mode,
    seriesContexts,
    refreshTick
  ]);

  const today = getEffectiveToday();
  const isTraditional = course.grading_mode === 'traditional';

  function getWeight(assignmentTypeId) {
    const w = assignmentWeights.find(w => w.assignment_type_id === assignmentTypeId);
    return w ? w.weight_percent : null;
  }

  function getWeightByName(name) {
    const type = assignmentTypes.find(
      item => item.name === name
    );

    return type ? getWeight(type.id) : null;
  }

  function getPdDueDate(
    week,
    submission = null
  ) {
    if (!week?.class_date) {
      return null;
    }

    let baseDueDate =
      String(
        week.class_date
      ).slice(0, 10);

    if (course?.pd_due_day) {
      const dayNames = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday'
      ];

      const targetDay =
        dayNames.indexOf(
          course.pd_due_day
        );

      if (targetDay >= 0) {
        const parts =
          baseDueDate
            .split('-')
            .map(Number);

        const date =
          new Date(
            parts[0],
            parts[1] - 1,
            parts[2],
            12,
            0,
            0
          );

        while (
          date.getDay() !==
          targetDay
        ) {
          date.setDate(
            date.getDate() + 1
          );
        }

        baseDueDate = [
          date.getFullYear(),
          String(
            date.getMonth() + 1
          ).padStart(2, '0'),
          String(
            date.getDate()
          ).padStart(2, '0')
        ].join('-');
      }
    }

    return addDaysToDateString(
      baseDueDate,
      Math.max(
        0,
        Number(
          submission
            ?.extension_days
        ) || 0
      )
    );
  }

  // Attendance stats
  const attendanceRequiredIdSet = new Set(
    attendanceRequiredWeekIds
  );

  const attendanceEnabledWeeks = course.group_based_attendance
    ? weeks.filter(w => attendanceRequiredIdSet.has(w.id))
    : weeks.filter(w => w.attendance_enabled);
  const attendancePastWeeks = attendanceEnabledWeeks.filter(
    w => w.class_date <= today
  );
  const attendancePastWeekIds = new Set(
    attendancePastWeeks.map(w => w.id)
  );
  const attendanceWeeks = {
    soFar: attendancePastWeeks.length,
    total: attendanceEnabledWeeks.length
  };
  const attendedWeekIds =
    new Set(
      attendance
        .filter(
          row =>
            row.present &&
            attendancePastWeekIds.has(
              row.week_id
            )
        )
        .map(
          row => row.week_id
        )
    );

  const attendedCount =
    attendedWeekIds.size;

  const attendanceWeight =
    Number(
      getWeightByName(
        'Attendance'
      )
    ) || 0;

  const attendanceAbsences =
    Math.max(
      0,
      attendancePastWeeks.length -
        attendedCount
    );

  const attendanceSettings =
    normalizeAttendanceSettings(
      course
    );

  /*
   * This is the contribution the student would receive
   * from Attendance if they accrued no further absences.
   *
   * It uses the full set of required attendance classes
   * because the instructor's Attendance calculation table
   * is a final-grade penalty table.
   */
  const attendanceCurrentContribution =
    attendanceEnabledWeeks.length > 0
      ? calculateAttendanceContribution({
          absences:
            attendanceAbsences,

          totalRequired:
            attendanceEnabledWeeks.length,

          weight:
            attendanceWeight,

          ...attendanceSettings
        })
      : 0;

  /*
   * Only the proportion of Attendance corresponding to
   * classes that have actually occurred is due so far.
   *
   * This makes Attendance consistent with CQ, PD, and
   * ordinary weighted assignments.
   */
  const attendanceDuePoints =
    attendanceEnabledWeeks.length > 0
      ? attendanceWeight *
        (
          attendancePastWeeks.length /
          attendanceEnabledWeeks.length
        )
      : 0;

  /*
   * The configured absence rules determine how many
   * Attendance points have already been lost.
   *
   * At the end of the semester:
   *
   *   duePoints = attendanceWeight
   *
   * so earnedPoints becomes exactly
   * attendanceCurrentContribution.
   */
  const attendancePenaltyPoints =
    attendanceWeight -
    attendanceCurrentContribution;

  const attendanceEarnedPoints =
    attendancePastWeeks.length > 0
      ? attendanceDuePoints -
        attendancePenaltyPoints
      : 0;

  // CQ stats
  const cqEnabledWeeks =
    course.cq_enabled === false
      ? []
      : weeks.filter(
          week =>
            week.cq_enabled
        );

  const cqPastWeeks =
    cqEnabledWeeks.filter(
      week =>
        week.class_date <=
        today
    );

  const cqPastWeekIds =
    new Set(
      cqPastWeeks.map(
        week =>
          week.id
      )
    );

  const cqWeeks = {
    soFar:
      cqPastWeeks.length,

    total:
      cqEnabledWeeks.length
  };

  const cqSubmittedPastWeekIds =
    new Set([
      ...cqSubmissions
        .filter(
          submission =>
            cqPastWeekIds
              .has(
                submission.week_id
              )
        )
        .map(
          submission =>
            submission.week_id
        ),

      ...cqManualSubmissionMarks
        .filter(
          mark =>
            cqPastWeekIds
              .has(
                mark.week_id
              )
        )
        .map(
          mark =>
            mark.week_id
        )
    ]);
  const cqSubmittedCount = cqSubmittedPastWeekIds.size;
  const cqWeight =
    Number(getWeightByName('Critical Questions')) || 0;
  const cqPointsPerWeek =
    cqEnabledWeeks.length > 0
      ? cqWeight / cqEnabledWeeks.length
      : 0;
  const cqEarnedPoints =
    cqSubmittedCount * cqPointsPerWeek;
  const cqDuePoints =
    cqPastWeeks.length * cqPointsPerWeek;

  // Question Improvement stats
  const qiRelevantAssignments =
    course.qi_enabled ===
    true
      ? qiAssignments.filter(
          assignment =>
            assignment
              .course_weeks
              ?.cq_enabled !==
            false
        )
      : [];


  const qiAssignmentById =
    new Map(
      qiRelevantAssignments.map(
        assignment => [
          String(
            assignment.id
          ),
          assignment
        ]
      )
    );


  const qiParticipantSubmissions =
    qiSubmissions.filter(
      submission =>
        qiSubmissionIncludesEnrolment(
          submission,
          enrolment.id
        ) &&
        qiAssignmentById.has(
          String(
            submission
              .cq_question_assignment_id
          )
        )
    );


  /*
   * Nominal group assignment determines the
   * normal denominator.
   */
  const qiAssignedWeekIds =
    new Set(
      qiRelevantAssignments
        .filter(
          assignment =>
            Number(
              assignment.stream
            ) ===
              Number(
                enrolment.stream
              ) &&
            Number(
              assignment
                .group_number
            ) ===
              Number(
                enrolment
                  .group_number
              )
        )
        .map(
          assignment =>
            assignment
              .course_week_id
        )
    );


  /*
   * Legitimate temporary participation with
   * another group still belongs in this
   * student's progress.
   */
  qiParticipantSubmissions
    .forEach(
      submission => {
        const assignment =
          qiAssignmentById.get(
            String(
              submission
                .cq_question_assignment_id
            )
          );

        if (
          assignment
            ?.course_week_id
        ) {
          qiAssignedWeekIds.add(
            assignment
              .course_week_id
          );
        }
      }
    );


  const qiPastAssignedWeekIds =
    new Set(
      qiRelevantAssignments
        .filter(
          assignment =>
            qiAssignedWeekIds.has(
              assignment
                .course_week_id
            ) &&
            assignment
              .course_weeks
              ?.class_date &&
            assignment
              .course_weeks
              .class_date <=
              today
        )
        .map(
          assignment =>
            assignment
              .course_week_id
        )
    );


  const qiSubmittedPastWeekIds =
    new Set(
      qiParticipantSubmissions
        .filter(
          submission =>
            submission
              .submitted ===
            true
        )
        .map(
          submission =>
            qiAssignmentById
              .get(
                String(
                  submission
                    .cq_question_assignment_id
                )
              )
              ?.course_week_id
        )
        .filter(
          weekId =>
            weekId &&
            qiPastAssignedWeekIds.has(
              weekId
            )
        )
    );


  const qiWeeks = {
    soFar:
      qiPastAssignedWeekIds.size,

    total:
      qiAssignedWeekIds.size
  };


  const qiSubmittedCount =
    qiSubmittedPastWeekIds.size;

  // PD stats
  const pdEnabledWeeks =
    course.pd_enabled === false
      ? []
      : weeks.filter(
          week =>
            week.pd_enabled
        );

  const pdPastDue =
    pdEnabledWeeks.filter(
      week => {
        const submission =
          pdSubmissions.find(
            row =>
              row.week_id ===
              week.id
          );

        const dueDate =
          getPdDueDate(
            week,
            submission
          );

        return (
          !!dueDate &&
          dueDate <= today
        );
      }
    );

  const pdPastDueIds =
    new Set(
      pdPastDue.map(
        week =>
          week.id
      )
    );

  const pdSubmittedCount =
    new Set(
      pdSubmissions
        .filter(
          submission =>
            isProgressSubmissionComplete(submission, submission.pd_feedback) &&
            pdPastDueIds.has(
              submission.week_id
            )
        )
        .map(
          submission =>
            submission.week_id
        )
    ).size;

  const pdTotal =
    pdEnabledWeeks.length;

  const pdWeight =
    Number(
      getWeightByName(
        'Philosophical Diary'
      )
    ) || 0;

  const pdPointsPerWeek =
    pdTotal > 0
      ? pdWeight / pdTotal
      : 0;

  const pdDuePoints =
    pdPastDue.length *
    pdPointsPerWeek;

  const pdEarnedPoints =
    pdPastDue.reduce(
      (sum, week) => {
        const feedback =
          pdFeedback.find(
            row =>
              row.submitted ===
                true &&
              row.pd_submissions
                ?.week_id ===
                week.id
          );

        const numericScore =
          Number(
            feedback?.score
          );

        if (
          !Number.isFinite(
            numericScore
          )
        ) {
          return sum;
        }

        return (
          sum +
          (
            numericScore /
            100
          ) *
          pdPointsPerWeek
        );
      },
      0
    );

  // Other assignments
  const progressEnrolment = {
    ...enrolment,

    course_id:
      enrolment.course_id ||
      course.id
  };

  const assignmentUnits =
    progressAssignmentContext
      ? buildProgressAssessmentUnits({
          courseId:
            course.id,

          enrolment:
            progressEnrolment,

          context:
            progressAssignmentContext,

          submissions:
            assignmentSubmissions,

          completions:
            assignmentCompletions
        })
      : [];

  const submissionByType =
    {};

  const nonSubmissionByType =
    {};

  assignmentUnits.forEach(
    unit => {
      const target =
        unit
          .requiresSubmission ===
          false
          ? nonSubmissionByType
          : submissionByType;

      const key =
        String(
          unit.typeId ||
          unit.typeName
        );

      if (!target[key]) {
        target[key] = {
          name:
            unit.typeName ||
            'Assignment',

          typeId:
            unit.typeId,

          units: [],

          submitted: 0,

          completed: 0,

          soFar: 0,

          total: 0,

          totalPoints: 0,

          earnedPoints: 0,

          requiresSubmission:
            unit
              .requiresSubmission !==
              false
        };
      }

      const data =
        target[key];

      data.units.push(
        unit
      );

      data.total +=
        1;

      const complete =
        unit
          .requiresSubmission ===
          false
          ? unit.completed
          : unit.completed;

      if (unit.requiresSubmission !== false && unit.submitted) {
        data.submitted += 1;
      }
      if (complete) {
        data.completed += 1;
      }

      if (
        complete ||
        (
          unit.dueDate &&
          String(
            unit.dueDate
          ).slice(0, 10) <=
            today
        )
      ) {
        data.soFar +=
          1;
      }
    }
  );

  Object.values(
    submissionByType
  ).forEach(
    data => {
      const weight =
        Number(
          getWeight(
            data.typeId
          )
        );

      const safeWeight =
        Number.isFinite(
          weight
        )
          ? weight
          : 0;

      const pointsPerUnit =
        data.total > 0
          ? safeWeight /
            data.total
          : 0;

      const dueUnits =
        data.units
          .filter(
            unit =>
              unit.dueDate &&
              String(
                unit.dueDate
              ).slice(0, 10) <=
                today
          );

      data.totalPoints =
        dueUnits.length *
        pointsPerUnit;

      data.earnedPoints =
        dueUnits.reduce(
          (sum, unit) => {
            const feedback =
              latestProgressFeedback(
                unit.submission
              );

            if (
              feedback
                ?.submitted !==
              true
            ) {
              return sum;
            }

            const individualOverride =
              assignmentGradeOverrides
                .find(
                  row =>
                    String(
                      row
                        .assignment_submission_id
                    ) ===
                    String(
                      unit
                        .submission
                        ?.id ||
                      ''
                    )
                );

            /*
             * An individual group-grade override
             * is the student's actual grade.
             *
             * If no override exists, use the
             * ordinary shared/group grade.
             */
            const score =
              Number(
                individualOverride
                  ?.score !==
                    null &&
                individualOverride
                  ?.score !==
                    undefined
                  ? individualOverride
                      .score
                  : feedback.score
              );

            if (
              !Number.isFinite(
                score
              )
            ) {
              return sum;
            }

            return (
              sum +
              (
                score /
                100
              ) *
              pointsPerUnit
            );
          },
          0
        );
    }
  );

  const ordinaryAssignmentEarnedPoints =
    Object.values(
      submissionByType
    ).reduce(
      (sum, data) =>
        sum +
        data.earnedPoints,
      0
    );

  const ordinaryAssignmentDuePoints =
    Object.values(
      submissionByType
    ).reduce(
      (sum, data) =>
        sum +
        data.totalPoints,
      0
    );

  const ordinaryAssignmentConfiguredWeight =
    Object.values(
      submissionByType
    ).reduce(
      (sum, data) =>
        sum +
        (
          Number(
            getWeight(
              data.typeId
            )
          ) ||
          0
        ),
      0
    );

  const traditionalEarnedPoints =
    ordinaryAssignmentEarnedPoints +
    (
      course
        .attendance_enabled !==
        false
        ? attendanceEarnedPoints
        : 0
    ) +
    (
      course
        .cq_enabled !==
        false
        ? cqEarnedPoints
        : 0
    ) +
    (
      course
        .pd_enabled !==
        false
        ? pdEarnedPoints
        : 0
    );

  const traditionalDuePoints =
    ordinaryAssignmentDuePoints +
    (
      course
        .attendance_enabled !==
        false
        ? attendanceDuePoints
        : 0
    ) +
    (
      course
        .cq_enabled !==
        false
        ? cqDuePoints
        : 0
    ) +
    (
      course
        .pd_enabled !==
        false
        ? pdDuePoints
        : 0
    );

  const traditionalConfiguredWeight =
    ordinaryAssignmentConfiguredWeight +
    (
      course
        .attendance_enabled !==
        false
        ? attendanceWeight
        : 0
    ) +
    (
      course
        .cq_enabled !==
        false
        ? cqWeight
        : 0
    ) +
    (
      course
        .pd_enabled !==
        false
        ? pdWeight
        : 0
    );

  const traditionalPercentSoFar =
    traditionalDuePoints > 0
      ? (
          traditionalEarnedPoints /
          traditionalDuePoints
        ) * 100
      : null;

  const menuOrderByType =
    new Map();

  assignmentTypeSettings
    .forEach(
      row => {
        const numeric =
          Number(
            row.menu_order
          );

        if (
          Number.isFinite(
            numeric
          )
        ) {
          menuOrderByType
            .set(
              String(
                row.assignment_type_id
              ),
              numeric
            );
        }
      }
    );

  const firstTypeIndex =
    new Map();

  courseAssignments
    .forEach(
      (
        assignment,
        index
      ) => {
        const key =
          String(
            assignment
              .assignment_type_id
          );

        if (
          !firstTypeIndex
            .has(key)
        ) {
          firstTypeIndex
            .set(
              key,
              index
            );
        }
      }
    );

  const orderedAssignmentProgress =
    [
      ...Object.entries(
        submissionByType
      ),

      ...Object.entries(
        nonSubmissionByType
      )
    ]
      .sort(
        (
          [, a],
          [, b]
        ) => {
          const aOrder =
            menuOrderByType
              .get(
                String(
                  a.typeId
                )
              );

          const bOrder =
            menuOrderByType
              .get(
                String(
                  b.typeId
                )
              );

          const aHasOrder =
            Number.isFinite(
              aOrder
            );

          const bHasOrder =
            Number.isFinite(
              bOrder
            );

          if (
            aHasOrder &&
            bHasOrder
          ) {
            return (
              aOrder -
                bOrder ||
              (
                firstTypeIndex
                  .get(
                    String(
                      a.typeId
                    )
                  ) ||
                0
              ) -
                (
                  firstTypeIndex
                    .get(
                      String(
                        b.typeId
                      )
                    ) ||
                  0
                )
            );
          }

          if (aHasOrder) {
            return -1;
          }

          if (bHasOrder) {
            return 1;
          }

          return (
            (
              firstTypeIndex
                .get(
                  String(
                    a.typeId
                  )
                ) ||
              0
            ) -
            (
              firstTypeIndex
                .get(
                  String(
                    b.typeId
                  )
                ) ||
              0
            )
          );
        }
      );

  // Self-eval stats
  const midEvalCloseDate =
    addDaysToDateString(
      course.mid_eval_close_date,
      Math.max(
        0,
        Number(
          course
            .mid_eval_extension_days
        )||0
      )
    );

  const finalEvalCloseDate =
    addDaysToDateString(
      course.final_eval_close_date,
      Math.max(
        0,
        Number(
          course
            .final_eval_extension_days
        )||0
      )
    );

  const midEvalStatus = () => {
    if (!course.mid_eval_open_date) return null;
    const response = selfEvalResponses.find(r => r.section === 'Mid-Semester');
    if (response?.status === 'Submitted') return 'Submitted';
    if (
      today >=
        course.mid_eval_open_date &&
      today <=
        midEvalCloseDate
    ) return 'Open';

    if (
      today >
        midEvalCloseDate
    ) return 'Closed';
    return 'Not yet open';
  };

  const finalEvalStatus = () => {
    if (!course.final_eval_open_date) return null;
    const response = selfEvalResponses.find(r => r.section === 'Final');
    if (response?.status === 'Submitted') return 'Submitted';
    if (
      today >=
        course.final_eval_open_date &&
      today <=
        finalEvalCloseDate
    ) return 'Open';

    if (
      today >
        finalEvalCloseDate
    ) return 'Closed';
    return 'Not yet open';
  };

  const midStatus = midEvalStatus();
  const finalStatus = finalEvalStatus();

  function statusColor(status) {
    if (status === 'Submitted') return '#216e45';
    if (status === 'Open') return '#2f6f9f';
    if (status === 'Closed') return '#a12a2a';
    return '#627d98';
  }

  if (loading) return <div style={styles.wrap}><div style={styles.muted}>Loading...</div></div>;

  return (
    <div style={styles.wrap}>
      <div style={styles.content}>
        <h2 style={styles.heading}>My Progress</h2>

        {seriesProgressLoading && (
          <div style={styles.seriesNotice}>
            Loading cumulative course progress…
          </div>
        )}

        {seriesProgressError && (
          <div style={styles.seriesError}>
            {seriesProgressError}
          </div>
        )}

        {seriesProgress && (
          <div style={styles.seriesBlock}>
            <div style={styles.seriesHeader}>
              <div>
                <div style={styles.seriesKicker}>
                  Continued course
                </div>

                <h3 style={styles.seriesTitle}>
                  Cumulative Progress
                </h3>
              </div>

              <div style={styles.seriesSemesters}>
                {seriesProgress
                  .semesters
                  .join(' → ')}
              </div>
            </div>

            <div style={styles.seriesHelp}>
              These counts continue from the most recent Restart boundary.
              Weighted grades and self-evaluations remain semester-specific.
            </div>

            {seriesProgress.attendance.total > 0 && (
              <ProgressItem
                label="Attendance"
                soFar={
                  seriesProgress
                    .attendance
                    .completed
                }
                soFarTotal={
                  seriesProgress
                    .attendance
                    .soFar
                }
                total={
                  seriesProgress
                    .attendance
                    .total
                }
                isTraditional={false}
              />
            )}

            {seriesProgress.cq.total > 0 && (
              <ProgressItem
                label="Questions Assignment"
                soFar={
                  seriesProgress
                    .cq
                    .completed
                }
                soFarTotal={
                  seriesProgress
                    .cq
                    .soFar
                }
                total={
                  seriesProgress
                    .cq
                    .total
                }
                isTraditional={false}
              />
            )}

            {seriesProgress.qi.total > 0 && (
              <ProgressItem
                label="Question Improvement"
                soFar={
                  seriesProgress
                    .qi
                    .completed
                }
                soFarTotal={
                  seriesProgress
                    .qi
                    .soFar
                }
                total={
                  seriesProgress
                    .qi
                    .total
                }
                isTraditional={false}
              />
            )}

            {seriesProgress.pd.total > 0 && (
              <ProgressItem
                label="Philosophical Diary"
                soFar={
                  seriesProgress
                    .pd
                    .completed
                }
                soFarTotal={
                  seriesProgress
                    .pd
                    .soFar
                }
                total={
                  seriesProgress
                    .pd
                    .total
                }
                isTraditional={false}
              />
            )}

            {Object.entries(
              seriesProgress.assignments
            ).map(
              ([name, progress]) => (
                <ProgressItem
                  key={
                    `series-${name}`
                  }
                  label={name}
                  soFar={
                    progress.completed
                  }
                  soFarTotal={
                    progress.soFar
                  }
                  total={
                    progress.total
                  }
                  isTraditional={false}
                />
              )
            )}
          </div>
        )}

        {seriesProgress && (
          <div style={styles.localHeading}>
            Current Semester
          </div>
        )}

        {isTraditional && (
          <div style={styles.item}>
            <div style={styles.itemTop}>
              <div style={styles.itemLabel}>
                Weighted Grade
              </div>

              <div style={styles.itemStats}>
                <span style={styles.statMain}>
                  {traditionalPercentSoFar !== null
                    ? `${traditionalPercentSoFar.toFixed(1)}% on weighted work due so far`
                    : 'No weighted assignments due yet'}
                </span>

                <span style={styles.statTotal}>
                  {traditionalEarnedPoints.toFixed(1)}
                  {' of '}
                  {traditionalDuePoints.toFixed(1)}
                  {' weighted points earned'}
                </span>

                <span style={styles.statTotal}>
                  Configured grading weights:{' '}
                  {traditionalConfiguredWeight}%
                  {' / 100%'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Attendance */}
        {course.attendance_enabled !== false && attendanceWeeks.total > 0 && (
          <ProgressItem
            label="Attendance"
            soFar={attendedCount}
            soFarTotal={attendanceWeeks.soFar}
            total={attendanceWeeks.total}
            isTraditional={isTraditional}
            earnedPoints={attendanceEarnedPoints}
            totalPoints={attendanceDuePoints}
            weightTotal={attendanceWeight}
            detail={
              `${attendedCount} of ${attendanceWeeks.soFar} required class${attendanceWeeks.soFar === 1 ? '' : 'es'} attended so far · ${attendanceAbsences} absence${attendanceAbsences === 1 ? '' : 's'}`
            }
          />
        )}

        {/* CQ */}
        {course.cq_enabled !== false && cqWeeks.total > 0 && (
          <ProgressItem
            label="Questions Assignment"
            soFar={cqSubmittedCount}
            soFarTotal={cqWeeks.soFar}
            total={cqWeeks.total}
            isTraditional={isTraditional}
            earnedPoints={cqEarnedPoints}
            totalPoints={cqDuePoints}
            weightTotal={cqWeight}
          />
        )}

        {/* Question Improvement */}
        {course.qi_enabled === true && (
          <ProgressItem
            label="Question Improvement"
            soFar={qiSubmittedCount}
            soFarTotal={qiWeeks.soFar}
            total={qiWeeks.total}
            isTraditional={false}
          />
        )}

        {/* PD */}
        {course.pd_enabled !== false && pdTotal > 0 && (
          <ProgressItem
            label="Philosophical Diary"
            soFar={pdSubmittedCount}
            soFarTotal={pdPastDue.length}
            total={pdTotal}
            isTraditional={isTraditional}
            earnedPoints={pdEarnedPoints}
            totalPoints={pdDuePoints}
            weightTotal={pdWeight}
          />
        )}

        {/* Other assignments in the same course-specific order
            used by the assignment menu. */}
        {orderedAssignmentProgress.map(
          ([key, data]) => {
            const noSubmission =
              data
                .requiresSubmission ===
              false;

            return (
              <ProgressItem
                key={key}
                label={data.name}
                soFar={data.completed}
                soFarTotal={
                  data.soFar
                }
                total={
                  data.total
                }
                noSubmission={
                  noSubmission
                }
                isTraditional={
                  noSubmission
                    ? false
                    : isTraditional
                }
                earnedPoints={
                  noSubmission
                    ? undefined
                    : data
                        .earnedPoints
                }
                totalPoints={
                  noSubmission
                    ? undefined
                    : data
                        .totalPoints
                }
                weightTotal={
                  noSubmission
                    ? undefined
                    : getWeight(
                        data.typeId
                      )
                }
              />
            );
          }
        )}

        {/* Self-evaluation */}
        {course.self_eval_enabled !== false && midStatus && (
          <div style={styles.item}>
            <div style={styles.itemLabel}>Mid-Term Self-Evaluation</div>
            <div style={{ ...styles.statusPill, color: statusColor(midStatus), background: statusColor(midStatus) + '18' }}>
              {midStatus}
            </div>
          </div>
        )}
        {course.self_eval_enabled !== false && finalStatus && (
          <div style={styles.item}>
            <div style={styles.itemLabel}>Final Self-Evaluation</div>
            <div style={{ ...styles.statusPill, color: statusColor(finalStatus), background: statusColor(finalStatus) + '18' }}>
              {finalStatus}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressItem({
  label,
  soFar,
  soFarTotal,
  total,
  noSubmission,
  isTraditional,
  earnedPoints,
  totalPoints,
  weightTotal,
  detail
}) {
  const pct = isTraditional
    ? (
        totalPoints > 0
          ? Math.round(
              (earnedPoints / totalPoints) * 100
            )
          : 0
      )
    : (
        soFarTotal > 0
          ? Math.round(
              (soFar / soFarTotal) * 100
            )
          : 0
      );

  const barPct = Math.max(
    0,
    Math.min(100, pct)
  );

  const barColor = pct === 100 ? '#216e45' : pct >= 75 ? '#2f6f9f' : pct >= 50 ? '#8a5b00' : '#a12a2a';

  return (
    <div style={styles.item}>
      <div style={styles.itemTop}>
        <div style={styles.itemLabel}>{label}</div>
        <div style={styles.itemStats}>
          {isTraditional ? (
            <>
              <span style={styles.statMain}>
                {earnedPoints.toFixed(1)} of {totalPoints.toFixed(1)} points so far
              </span>
              {detail && (
                <span
                  style={
                    styles.statTotal
                  }
                >
                  {detail}
                </span>
              )}

              <span
                style={
                  styles.statTotal
                }
              >
                {weightTotal !== null
                  ? `${weightTotal}% of total grade`
                  : ''}
              </span>
            </>
          ) : (
            <>
              <span style={styles.statMain}>{soFar} of {soFarTotal} so far</span>
              <span style={styles.statTotal}>{total} total</span>
            </>
          )}
        </div>
      </div>
      <div style={styles.barBg}>
        <div style={{ ...styles.barFill, width: `${barPct}%`, background: barColor }} />
      </div>
    </div>
  );
}

const styles = {
  wrap: { minHeight: 0, background: 'transparent' },
  content: { padding: 0, maxWidth: 'none', margin: 0 },
  muted: { color: '#627d98', fontSize: '14px' },
  heading: { margin: '0 0 20px', fontSize: '26px', color: '#243b53', fontWeight: '700' },

  seriesBlock: {
    marginBottom: '24px',
    padding: '16px',
    border: '1px solid #b8cfe0',
    borderRadius: '7px',
    background: '#f8fbfd'
  },

  seriesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: '14px',
    flexWrap: 'wrap',
    marginBottom: '8px'
  },

  seriesKicker: {
    color: '#2f6f9f',
    fontSize: '10px',
    fontWeight: '800',
    letterSpacing: '0.1em',
    textTransform: 'uppercase'
  },

  seriesTitle: {
    margin: '3px 0 0',
    color: '#243b53',
    fontSize: '19px'
  },

  seriesSemesters: {
    color: '#627d98',
    fontSize: '12px'
  },

  seriesHelp: {
    marginBottom: '13px',
    color: '#627d98',
    fontSize: '12px',
    lineHeight: 1.45
  },

  seriesNotice: {
    marginBottom: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    background: '#f7f9fa',
    border: '1px solid #d9e2ec',
    color: '#627d98',
    fontSize: '13px'
  },

  seriesError: {
    marginBottom: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    color: '#a12a2a',
    fontSize: '13px'
  },

  localHeading: {
    margin: '0 0 10px',
    color: '#52616b',
    fontSize: '12px',
    fontWeight: '800',
    letterSpacing: '0.08em',
    textTransform: 'uppercase'
  },

  item: {
    background: 'white', border: '1px solid #d9e2ec', borderRadius: '6px',
    padding: '14px 16px', marginBottom: '10px'
  },
  itemTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  itemLabel: { fontWeight: '700', fontSize: '15px', color: '#243b53' },
  itemStats: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' },
  statMain: { fontSize: '14px', fontWeight: '600', color: '#243b53' },
  statTotal: { fontSize: '12px', color: '#627d98' },
  barBg: { height: '6px', background: '#edf1f5', borderRadius: '3px', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '3px', transition: 'width 0.3s' },
  noSubNote: { fontSize: '12px', color: '#627d98', marginTop: '6px' },
  statusPill: {
    display: 'inline-block', borderRadius: '999px',
    padding: '3px 10px', fontSize: '13px', fontWeight: '700'
  }
};
